import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  fetchFavorites,
  fetchAssetsByCategory,
  postRequestSignedUrls
} from '../../../assets/requests'
import { useUserInfo } from '../../Authentication/hooks'
import {
  createNavigationHandlers,
  sortAssetsByDateFromKeys,
  removeAssetFromCollection,
  formatAssetDate,
  createModalProps,
  handleAsyncError
} from './utils/categoryUtils'
import placeholderImg from '../../../assets/images/camera.jpg'
import '../../../assets/stylesheets/gallery-style.css'
import '../../../assets/stylesheets/assets.css'
import '../../../assets/stylesheets/duplicates.css'
import '../../../assets/stylesheets/modal.css'
import { handleModalOpen } from '../Modal/modals'
import { ModalHtml } from '../Modal/modals'
import upArrow from '../../../assets/images/up-arrow.png'
import backArrow from '../../../assets/images/back-arrow3.png'
import homeIcon from '../../../assets/images/home-icon.png'

const FavoritesCategory = () => {
  const [categoryDetails, setCategoryDetails] = useState(null)
  const [modalActive, setModalActive] = useState(false)
  const [currentAssetId, setCurrentAssetId] = useState(null)
  const [assets, setAssets] = useState({})
  const [refreshTrigger, setRefreshTrigger] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const dispatch = useDispatch()
  const { userId } = useUserInfo()
  const navigate = useNavigate()

  const { handleHomeClick, handleBackClick, handleBackToTop } =
    createNavigationHandlers(navigate)

  const signAssets = useCallback(
    async assets => {
      if (!userId) {
        return assets
      }

      let assetIds = Object.keys(assets)
      let thumbnailKeys = []
      let s3Uris = []
      let s3UriIdx = []

      assetIds.forEach((id, idx) => {
        const asset = assets[id]
        if (!asset.thumbnailUrl && asset.thumbnails && asset.thumbnails.length > 0) {
          thumbnailKeys.push(asset.thumbnails[1])
        }

        if (!asset.fileUrl) {
          s3Uris.push(asset.s3uri)
          s3UriIdx.push(idx)
        }
      })

      const [signedThumbnailUrls, signedS3Uris] = await Promise.all([
        thumbnailKeys.length > 0
          ? postRequestSignedUrls({ userId, payload: thumbnailKeys })
          : [],
        s3Uris.length > 0
          ? postRequestSignedUrls({ userId, payload: s3Uris })
          : []
      ])

      assetIds.forEach((id, idx) => {
        if (signedThumbnailUrls[idx]) {
          assets[id].thumbnailUrl = signedThumbnailUrls[idx]
        }

        const s3UriSignedIndex = s3UriIdx.indexOf(idx)
        if (
          s3UriSignedIndex !== -1 &&
          signedS3Uris &&
          signedS3Uris[s3UriSignedIndex]
        ) {
          assets[id].fileUrl = signedS3Uris[s3UriSignedIndex]
        }
      })

      return assets
    },
    [userId]
  )

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError('')
        const details = await fetchFavorites()
        setCategoryDetails(details)
        if (details) {
          const rawAssets = await fetchAssetsByCategory({
            categoryId: details.id,
            userId: userId
          })
          const signedAssets = await signAssets(rawAssets)
          setAssets(signedAssets)
        }
      } catch (error) {
        const errorMessage = handleAsyncError(error, 'fetching assets')
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [userId, signAssets, refreshTrigger])

  if (error) {
    return <div className="error-message">Error: {error}</div>
  }

  if (loading) {
    return <div>Loading...</div>
  }

  if (!categoryDetails || !Object.keys(assets).length) {
    return (
      <div className="duplicates-category-container">
        <h2 className="row-title-duplicates">Favorites</h2>
        <div className="go-back">
          <img className="nav-icon-home home fav" alt="Go to base of navigation" src={homeIcon} onClick={handleHomeClick} />
          <img className="nav-icon-back back" alt="Go back one level." src={backArrow} onClick={handleBackClick} />
          <img className="nav-icon-top top" alt="Go back to top." src={upArrow} onClick={handleBackToTop} />
        </div>
        <div className="gallery-container">
          <div className="empty-state-message">
            <p>No favorite images selected yet.</p>
          </div>
        </div>
      </div>
    )
  }

  const closeModal = () => {
    setModalActive(false)
  }

  const handleAssetDeleted = deletedAssetId => {
    const updatedAssets = removeAssetFromCollection(assets, deletedAssetId)
    setAssets(updatedAssets)
    closeModal()
    setRefreshTrigger(!refreshTrigger)
  }

  const sortedAssetIds = sortAssetsByDateFromKeys(assets, 'asc')

  return (
    <div className="duplicates-category-container">
      <h2 className="row-title-duplicates">Favorites</h2>
      <div className="go-back">
        <img className="nav-icon-home home fav" alt="Go to base of navigation" src={homeIcon} onClick={handleHomeClick} />
        <img className="nav-icon-back back" alt="Go back one level." src={backArrow} onClick={handleBackClick} />
        <img className="nav-icon-top top" alt="Go back to top." src={upArrow} onClick={handleBackToTop} />
      </div>

      <div className="gallery-container">
        {sortedAssetIds.map(assetId => {
          const asset = assets[assetId]
          return (
            <div key={asset.id} className="gallery-thumb-duplicates">
              <div
                className="gallery-thumb2"
                style={{
                  backgroundImage: `url('${asset.thumbnailUrl || placeholderImg}')`
                }}
                onClick={() => {
                  setCurrentAssetId(asset.id)
                  const modalProps = createModalProps(
                    dispatch,
                    modalActive,
                    assets,
                    asset.id
                  )
                  handleModalOpen(modalProps)
                  setModalActive(true)
                }}
              >
                <div className="duplicates-date">
                  {formatAssetDate(asset.content?.date)}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {modalActive && (
        <ModalHtml
          state={{
            modalActive,
            currentAsset: currentAssetId,
            modalStatus: 'Image',
            assets
          }}
          onAssetDeleted={handleAssetDeleted}
          dispatch={dispatch}
          onClose={closeModal}
        />
      )}
    </div>
  )
}

export default FavoritesCategory
